import type {
	API_GetOrganizationDetailsResponse,
	API_GetOrganizationsListRequest,
	API_GetOrganizationsListResponse,
	API_ResetOrganizationTrialResponse,
	API_SyncSubscriptionResponse,
	OrganizationDetails,
	OrganizationListItem,
	PaginationMeta,
	SubscriptionSyncResult,
} from "@pilatestime-admin/shared-types";
import { queryOptions, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

export interface OrganizationsListData {
	organizations: OrganizationListItem[];
	pagination: PaginationMeta;
}

/**
 * Fetch organizations list from the API
 */
export const fetchOrganizations = (
	token: string,
	params?: API_GetOrganizationsListRequest,
): Promise<OrganizationsListData> =>
	api
		.get<API_GetOrganizationsListResponse>("/organizations", {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params,
		})
		.then((res) => res.data.data);

/**
 * Query options for fetching organizations list
 */
export const fetchOrganizationsQueryOptions = (
	token: string,
	params?: API_GetOrganizationsListRequest,
) =>
	queryOptions({
		queryKey: ["organizations", params],
		queryFn: () => fetchOrganizations(token, params),
		staleTime: 1 * 60 * 1000, // 1 minute
	});

/**
 * Fetch organization details by ID
 */
export const fetchOrganizationById = (
	token: string,
	organizationId: string,
): Promise<OrganizationDetails> =>
	api
		.get<API_GetOrganizationDetailsResponse>(
			`/organizations/${organizationId}`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data.data);

/**
 * Query options for fetching organization details
 */
export const fetchOrganizationByIdQueryOptions = (
	token: string,
	organizationId: string,
) =>
	queryOptions({
		queryKey: ["organizations", organizationId],
		queryFn: () => fetchOrganizationById(token, organizationId),
		staleTime: 1 * 60 * 1000, // 1 minute
	});

/**
 * Reset organization trial
 */
export const resetOrganizationTrial = (
	token: string,
	organizationId: string,
): Promise<OrganizationDetails> =>
	api
		.post<API_ResetOrganizationTrialResponse>(
			`/organizations/${organizationId}/reset-trial`,
			{},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data.data);

/**
 * Mutation hook for resetting organization trial
 */
export const useResetOrganizationTrial = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
		}: {
			token: string;
			organizationId: string;
		}) => resetOrganizationTrial(token, organizationId),
		toastMessages: {
			loading: t("organizations.toasts.resettingTrial"),
			success: t("organizations.toasts.resetTrialSuccess"),
			error: t("organizations.toasts.resetTrialError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate organization details to refetch with updated trial status
			queryClient.invalidateQueries({
				queryKey: ["organizations", variables.organizationId],
			});
			// Also invalidate list
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
		},
	});
};

/**
 * Sync the organization's platform subscription from live Stripe state into the
 * Main DB mirror (operator-triggered per-organization reconciliation).
 */
export const syncOrganizationSubscription = (
	token: string,
	organizationId: string,
): Promise<SubscriptionSyncResult> =>
	api
		.post<API_SyncSubscriptionResponse>(
			`/organizations/${organizationId}/subscription/sync`,
			{},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data.data);

/**
 * Mutation hook for syncing an organization's platform subscription
 */
export const useSyncOrganizationSubscription = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
		}: {
			token: string;
			organizationId: string;
		}) => syncOrganizationSubscription(token, organizationId),
		toastMessages: {
			loading: t("organizations.toasts.syncingSubscription"),
			success: t("organizations.toasts.syncSubscriptionSuccess"),
			error: t("organizations.toasts.syncSubscriptionError"),
		},
		onSuccess: (_data, variables) => {
			// Refetch organization details (and list) to reflect the reconciled state
			queryClient.invalidateQueries({
				queryKey: ["organizations", variables.organizationId],
			});
			queryClient.invalidateQueries({ queryKey: ["organizations"] });
		},
	});
};
